import { useId, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Clock,
  FileText,
  RefreshCw,
  ScanLine,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';

import { DocValidadeBadge } from '@/modulos/operacao/comum/DocValidadeBadge';
import { mostrarAviso } from '@/modulos/operacao/comum/cadastroIndisponivel';
import { cn } from '@/nucleo/utilitarios';
import { situacaoDeValidade } from '@/nucleo/validade';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Progress } from '@/ui/base/progress';

import { AVISO_DE_ENVIO_INDISPONIVEL } from './avisos';
import type { DocumentoReutilizavel, ItemDoChecklist, SituacaoDoChecklist } from './contrato';
import { ReutilizarDocumentoModal } from './ReutilizarDocumentoModal';

const CONFIGURACAO_POR_SITUACAO: Record<
  SituacaoDoChecklist,
  { icon: LucideIcon; cor: string; rotulo: string }
> = {
  aprovado: { icon: Check, cor: 'text-success-text', rotulo: 'Aprovado' },
  enviado: { icon: Clock, cor: 'text-warning-text', rotulo: 'Enviado' },
  aprovacao_pendente: { icon: ScanLine, cor: 'text-link', rotulo: 'Aprovação pendente' },
  pendente: { icon: FileText, cor: 'text-muted-foreground', rotulo: 'Pendente' },
  rejeitado: { icon: X, cor: 'text-destructive-text', rotulo: 'Rejeitado' },
  vencido: { icon: AlertTriangle, cor: 'text-warning-text', rotulo: 'Vencido' },
};

/**
 * Ordem de criticidade da validade, a do protótipo: vencido → vencendo →
 * vigente → sem data → sem envio. Empate mantém a ordem do requisito.
 */
const PESO_DA_VALIDADE = { vencido: 0, vencendo: 1, ok: 2, sem_data: 3, sem_envio: 4 } as const;

function pesoDaValidade(item: ItemDoChecklist): number {
  if (!item.documento) return PESO_DA_VALIDADE.sem_envio;
  const situacao = situacaoDeValidade(item.documento.validadeAte);
  return situacao === null ? PESO_DA_VALIDADE.sem_data : PESO_DA_VALIDADE[situacao];
}

interface DocumentChecklistProps {
  itens: ItemDoChecklist[];
  title?: string;
  /** Linha sob o título (ex.: marca e modelo do veículo, CPF mascarado). */
  subtitulo?: string | null;
  readOnly?: boolean;
  /** Documentos aprovados antes, que o "Reutilizar" oferece por tipo. */
  reutilizaveis?: DocumentoReutilizavel[];
  /**
   * Com `false`, o "Reutilizar" não é renderizado (regra 7 de interface): na
   * tela que já grava o envio (Documentos), oferecer uma reutilização que só
   * avisa seria uma ação que não conclui ao lado de uma que conclui. Sem
   * valor, aparece como no protótipo (Veículo e Monitor, que ainda só avisam).
   */
  ofereceReutilizar?: boolean;
  /**
   * Envio real. Com ele, "Enviar" chama esta função; sem ele, mostra o aviso
   * de indisponibilidade (telas que ainda não gravam).
   */
  onEnviar?: (item: ItemDoChecklist) => void;
  /**
   * Com valor, o cartão pode ser recolhido pelo título, e começa recolhido
   * quando `true`. Sem valor, fica sempre aberto (comportamento do protótipo).
   */
  recolhidoInicialmente?: boolean;
}

/**
 * Checklist de documentos do credenciado, de um veículo ou de um monitor —
 * o `DocumentChecklist` do protótipo, reaproveitado nas três telas como lá:
 * cartão com contagem e barra de progresso, uma linha por requisito com o
 * ícone e a cor da situação, selo de OCR, arquivo enviado, selo de validade
 * e as ações inline "Enviar" e "Reutilizar" em quem está pendente ou foi
 * rejeitado.
 *
 * O "Enviar" só grava quando a tela passa `onEnviar` (Documentos, ligada à
 * API em `portal-credenciado-documentos`); aí quem diz se o item aceita envio
 * é a API (`podeEnviar`, que inclui Vencido). Sem `onEnviar` — Veículo e
 * Monitor, ainda sem rota — "Enviar" mostra o aviso de indisponibilidade em
 * vez de abrir um seletor de arquivo que não levaria o arquivo a lugar nenhum.
 * "Reutilizar" abre a escolha do documento, que é leitura, e a confirmação
 * mostra o mesmo aviso; não é oferecido em Vencido, nem quando a tela passa
 * `ofereceReutilizar={false}` (Documentos, até a reutilização gravar).
 */
export function DocumentChecklist({
  itens,
  title = 'Documentos Necessários',
  subtitulo,
  readOnly,
  reutilizaveis = [],
  ofereceReutilizar = true,
  onEnviar,
  recolhidoInicialmente,
}: DocumentChecklistProps) {
  const [reutilizarTipo, setReutilizarTipo] = useState<string | null>(null);
  const recolhivel = recolhidoInicialmente !== undefined;
  const [recolhido, setRecolhido] = useState(recolhidoInicialmente === true);
  const idDoConteudo = useId();

  const total = itens.length;
  const enviados = itens.filter((item) => item.situacao !== 'pendente').length;
  const progresso = total > 0 ? Math.round((enviados / total) * 100) : 0;
  const documentacaoCompleta = itens
    .filter((item) => item.obrigatorio)
    .every((item) => item.situacao === 'enviado' || item.situacao === 'aprovado');

  const ordenados = itens
    .map((item, indice) => ({ item, indice, peso: pesoDaValidade(item) }))
    .sort((a, b) => a.peso - b.peso || a.indice - b.indice)
    .map(({ item }) => item);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <CardTitle className="text-base">
                {recolhivel ? (
                  <button
                    type="button"
                    className="focus-visible:ring-ring -mx-1 flex items-center gap-1 rounded px-1 text-left focus-visible:outline-hidden focus-visible:ring-2"
                    aria-expanded={!recolhido}
                    aria-controls={idDoConteudo}
                    onClick={() => setRecolhido((atual) => !atual)}
                  >
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform',
                        recolhido && '-rotate-90',
                      )}
                      aria-hidden="true"
                    />
                    {title}
                  </button>
                ) : (
                  title
                )}
              </CardTitle>
              {subtitulo && (
                <p className="text-muted-foreground mt-0.5 truncate text-xs">{subtitulo}</p>
              )}
            </div>
            <span className="text-muted-foreground shrink-0 text-xs">
              {enviados}/{total} documentos
            </span>
          </div>
          <Progress
            value={progresso}
            className="h-2"
            aria-label={`${enviados} de ${total} documentos enviados`}
          />
        </CardHeader>
        <CardContent id={idDoConteudo} hidden={recolhido} className="space-y-2">
          {itens.length === 0 && (
            <p className="text-muted-foreground py-6 text-center text-sm">
              Nenhum documento obrigatório cadastrado para este tipo.
            </p>
          )}
          {ordenados.map((item) => {
            const config = CONFIGURACAO_POR_SITUACAO[item.situacao];
            const Icon = config.icon;
            const pendenteOuRejeitado =
              item.situacao === 'pendente' || item.situacao === 'rejeitado';
            const podeEnviar = !readOnly && (item.podeEnviar ?? pendenteOuRejeitado);
            // Com vários cartões na tela (um por veículo, colaborador), o nome do
            // cartão entra no rótulo para o leitor de tela distinguir os botões.
            const alvo = onEnviar ? `${item.nome} — ${title}` : item.nome;

            return (
              <div
                key={item.id}
                className={cn(
                  'flex flex-wrap items-center gap-3 rounded-lg border p-3',
                  item.situacao === 'pendente' &&
                    item.obrigatorio &&
                    'border-warning/30 bg-warning/5',
                  item.situacao === 'aprovado' && 'border-success/30 bg-success/5',
                  item.situacao === 'rejeitado' && 'border-destructive/30 bg-destructive/5',
                  item.situacao === 'aprovacao_pendente' && 'border-primary/30 bg-primary/5',
                )}
              >
                <div
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                    item.situacao === 'pendente' ? 'bg-muted' : 'bg-background',
                  )}
                >
                  <Icon className={cn('h-4 w-4', config.cor)} aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {item.nome}
                    {item.obrigatorio && (
                      <>
                        <span className="text-destructive-text ml-1" aria-hidden="true">
                          *
                        </span>
                        <span className="sr-only"> (obrigatório)</span>
                      </>
                    )}
                    {item.ocrHabilitado && (
                      <Badge variant="outline" className="ml-2 gap-1 text-[10px]">
                        <ScanLine className="h-2.5 w-2.5" aria-hidden="true" /> OCR
                      </Badge>
                    )}
                  </p>
                  {item.descricao && (
                    <p className="text-muted-foreground truncate text-xs">{item.descricao}</p>
                  )}
                  {item.documento && (
                    <p className="text-link mt-0.5 flex items-center gap-1 text-xs">
                      <FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.documento.arquivoNome}</span>
                    </p>
                  )}
                </div>
                <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto sm:shrink-0">
                  {item.documento?.validadeAte && (
                    <DocValidadeBadge
                      validade={item.documento.validadeAte}
                      hideWhenEmpty
                      className="text-[10px]"
                    />
                  )}
                  <span className={cn('text-xs font-medium', config.cor)}>{config.rotulo}</span>
                  {podeEnviar && (
                    <>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-8 gap-1"
                        aria-label={`Enviar ${alvo}`}
                        onClick={() =>
                          onEnviar ? onEnviar(item) : mostrarAviso(AVISO_DE_ENVIO_INDISPONIVEL)
                        }
                      >
                        <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                        Enviar
                      </Button>
                      {ofereceReutilizar && pendenteOuRejeitado && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1"
                          aria-label={`Reutilizar documento para ${alvo}`}
                          onClick={() => setReutilizarTipo(item.nome)}
                        >
                          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                          Reutilizar
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {!documentacaoCompleta && !readOnly && (
            <p className="text-warning-text mt-2 flex items-center gap-1 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Envie todos os documentos obrigatórios para manter o cadastro regularizado.
            </p>
          )}
        </CardContent>
      </Card>

      {reutilizarTipo && (
        <ReutilizarDocumentoModal
          open
          onOpenChange={(aberto) => {
            if (!aberto) setReutilizarTipo(null);
          }}
          tipoDocumento={reutilizarTipo}
          documentos={reutilizaveis.filter(
            (documento) => documento.tipo.toLowerCase() === reutilizarTipo.toLowerCase(),
          )}
        />
      )}
    </>
  );
}
